import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import api, { getStorageUrl } from '../../api/axios';
import toast from 'react-hot-toast';
import {
  Info,
  Calendar,
  CheckCircle2,
  XCircle,
  Printer,
  Share2,
  History,
  ShieldAlert,
  Headphones,
  FileText,
  Download,
  Check,
  Circle,
  Camera,
  Plus,
  Loader2,
  AlertCircle,
  Save,
  Clock
} from 'lucide-react';
import styles from './InspectionClearance.module.css';
import Footer from '../../components/Common/Footer/Footer';
import NotFound from '../Error/NotFound';

const InspectionClearance = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [inspection, setInspection] = useState(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Form State
  const [clearanceStatus, setClearanceStatus] = useState('pending');
  const [actualDate, setActualDate] = useState('');
  const [remarks, setRemarks] = useState('');

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const fetchInspection = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/inspections/${id}`);
      if (response.data.success) {
        const data = response.data.data;
        setInspection(data);
        setClearanceStatus(data.clearance_status || 'pending');
        setActualDate(data.actual_inspection_date || '');
        setRemarks(data.remarks || '');
      }
    } catch (error) {
      toast.error('Failed to load inspection details');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchInspection();
  }, [fetchInspection]);

  const handleUpdateClearance = async (targetStatus = null) => {
    setSaveLoading(true);
    const finalStatus = targetStatus || clearanceStatus;

    try {
      const response = await api.put(`/inspections/${id}`, {
        actual_inspection_date: actualDate,
        clearance_status: finalStatus,
        remarks: remarks
      });

      if (response.data.success) {
        toast.success(response.data.message || 'Inspection updated successfully');
        setInspection(response.data.data);
        if (targetStatus) setClearanceStatus(targetStatus);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update clearance');
    } finally {
      setSaveLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={styles['inspection-clearance-container']}>
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}
        <div className={styles['content-wrapper']}>
          <Header onMenuClick={toggleSidebar} />
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: '20px' }}>
            <Loader2 size={48} className={styles['spinner']} color="#f59e0b" />
            <p style={{ color: '#64748b', fontWeight: 600 }}>Loading detailed inspection records...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!inspection) {
    return <NotFound />;
  }

  const isApproved = inspection.clearance_status === 'approved';
  const isRejected = inspection.clearance_status === 'rejected';

  return (
    <div className={`${styles['inspection-clearance-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={toggleSidebar} />

        <main className={styles['main-content']}>
          <Breadcrumbs />

          <header className={styles['clearance-header']}>
            <div className={styles['title-section']}>
              <h1>Inspection Detail: <span className={styles['highlight-id']}>#{inspection.id}</span></h1>
              <div className={styles['status-chips']}>
                <span className={`${styles['status-chip']} ${isApproved ? styles['chip-approved'] : isRejected ? styles['chip-rejected'] : styles['chip-progress']}`}>
                  <span className={styles['dot']} style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: isApproved ? '#10b981' : isRejected ? '#ef4444' : '#d97706'
                  }}></span>
                  {inspection.clearance_status ? inspection.clearance_status.toUpperCase() : 'IN-PROGRESS'}
                </span>

              </div>
            </div>

          </header>

          <div className={styles['clearance-grid']}>
            <div className={styles['left-column']}>
              <section className={styles['clearance-card']}>
                <div className={styles['card-header']}>
                  <Info size={18} className={styles['header-icon']} />
                  <h3>Inspection Summary</h3>
                  <span className={styles['timestamp']} style={{ marginLeft: 'auto' }}>
                    Scheduled on {new Date(inspection.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </span>
                </div>
                <div className={styles['card-body']}>
                  <div className={styles['batch-info-grid']}>
                    <div className={styles['info-item']}>
                      <label>Vendor</label>
                      <span>{inspection.vendor?.vendor_name || '---'}</span>
                    </div>
                    <div className={styles['info-item']}>
                      <label>Agency Type</label>
                      <span style={{ color: '#f59e0b', fontWeight: 700 }}>{inspection.agency_type}</span>
                    </div>
                    <div className={styles['info-item']}>
                      <label>LOI Number</label>
                      <span>{inspection.purchase_order?.po_number || '---'}</span>
                    </div>
                    <div className={styles['info-item']}>
                      <label>Project</label>
                      <span>{inspection.project?.project_name || '---'}</span>
                    </div>
                    <div className={styles['info-item']}>
                      <label>Contact Person</label>
                      <span>{inspection.contact_person_name || '---'}</span>
                    </div>
                    <div className={styles['info-item']}>
                      <label>Notified Date</label>
                      <span>{new Date(inspection.notified_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>
                    </div>
                  </div>
                </div>
              </section>

              <section className={styles['clearance-card']}>
                <div className={styles['card-header']}>
                  <ShieldAlert size={18} className={styles['header-icon']} />
                  <h3>Clearance & Results</h3>
                </div>
                <div className={styles['card-body']}>
                  <div className={styles['form-row']}>
                    <div className={styles['form-group']}>
                      <label>Actual Inspection Date</label>
                      <input
                        type="date"
                        value={actualDate}
                        onChange={(e) => setActualDate(e.target.value)}
                      />
                    </div>
                    <div className={styles['form-group']}>
                      <label>Clearance Status</label>
                      <select
                        value={clearanceStatus}
                        onChange={(e) => setClearanceStatus(e.target.value)}
                      >
                        <option value="pending">Pending Review</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </div>
                  </div>
                  <div className={styles['form-group']}>
                    <label>Detailed Remarks</label>
                    <textarea
                      placeholder="Enter quality inspection notes, defect ratios, or observations..."
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                    ></textarea>
                  </div>
                </div>
              </section>

              <section className={styles['timeline-section']}>
                <div className={styles['section-title']}>
                  <History size={20} color="#f59e0b" />
                  <h3>Inspection Log</h3>
                </div>
                <div className={styles['timeline-container']}>
                  {inspection.cleared_by && (
                    <div className={styles['timeline-item-clearance']}>
                      <div className={[styles['timeline-dot'], styles['active']].join(' ')}>
                        <Check size={18} />
                      </div>
                      <div className={styles['timeline-content']}>
                        <div className={styles['timeline-header']}>
                          <h4>Clearance Finalized</h4>
                          <span className={styles['time']}>{new Date(inspection.updated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        <div className={styles['timeline-body']}>
                          <p>Status set to '{inspection.clearance_status}' by {inspection.cleared_by_user?.name || 'Authorized Personnel'}.</p>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className={styles['timeline-item-clearance']}>
                    <div className={styles['timeline-dot']}>
                      <Clock size={18} />
                    </div>
                    <div className={styles['timeline-content']}>
                      <div className={styles['timeline-header']}>
                        <h4>Inspection Initialized</h4>
                        <span className={styles['time']}>{new Date(inspection.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>
                      </div>
                      <div className={styles['timeline-body']}>
                        <p>Scheduled by {inspection.creator?.name || 'System'}. Notification sent to {inspection.agency_type}.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </div>

            <div className={styles['sidebar-section']}>




              {inspection.purchase_order?.po_document_filepath && (
                <section className={[styles['clearance-card'], styles['documents-card']].join(' ')}>
                  <div className={styles['card-header']} style={{ padding: '0 0 16px 0', borderBottom: '1px solid #f1f5f9' }}>
                    <h3>Related Documents</h3>
                  </div>
                  <div className={styles['card-body']} style={{ padding: '20px 0 0 0' }}>
                    <div className={styles['doc-list']}>
                      <a
                        href={getStorageUrl(inspection.purchase_order.po_document_filepath)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles['doc-item']}
                      >
                        <div className={styles['doc-icon']}>
                          <FileText size={20} color="#ef4444" />
                        </div>
                        <div className={styles['doc-info']}>
                          <span className={styles['doc-name']}>{inspection.purchase_order.po_document_filename || 'LOI.pdf'}</span>
                          <span className={styles['doc-meta']}>View Document</span>
                        </div>
                        <Download size={16} className={styles['download-icon']} />
                      </a>
                    </div>
                  </div>
                </section>
              )}
            </div>
          </div>

          <div className={styles['header-actions-btns']}>
            <button
              className={styles['btn-save-draft']}
              onClick={() => handleUpdateClearance()}
              disabled={saveLoading}
            >
              {saveLoading ? <Loader2 size={18} className={styles['spinner']} /> : <Save size={18} />}
              <span>Save Progress</span>
            </button>
            <button
              className={styles['btn-approve']}
              onClick={() => handleUpdateClearance('approved')}
              disabled={saveLoading}
            >
              {saveLoading ? <Loader2 size={18} className={styles['spinner']} /> : <Check size={18} />}
              <span>Approve Inspection</span>
            </button>
          </div>

          <Footer />
        </main>
      </div>
    </div>
  );
};

export default InspectionClearance;
